import {
    APPLICANT,
    CCO,
    CLUSTER_ADMIN,
    SECRETARY,
    DRAFT,
    RETURNED,
    SUBMITTED,
    RE_SUBMITTED,
    DISTRIBUTED,
    userRoles,
    APPROVED,
    ACKNOWLEDGED,
    DISAPPROVED,
    TAB_COMMENT,
    TAB_AMENDMENT,
    TAB_REVIEWER_COMMENT,
    TAB_CONSOLIDATION,
    COMPLETED,
    TAB_MEETINGARRANGE,
    REVIEWER,
} from './lookup';
import _, { cloneDeep, isEmpty } from 'lodash';
import CommonRegex from 'constants/CommonRegex';
import { getStore } from 'store';
import moment from 'moment';
import api from 'api/call';
import { CTC, CTI, PostApprovalFormType } from 'utility/postApprovalLookUp';
import {
    LOGOUT_USER,
    DELETE_REMOVE_REQUEST,
    FETCH_NOTIFICATION_COUNT_OF_UNREAD,
    SET_APPLICATION_DETAIL_POST_DATA,
} from 'store/key';
import { LOGIN_SESSION_EXPIRED, MULTIPLE_LOGIN_LOGOUT } from './frontendMessage';
import { DATA_NOT_FOUND, EXPIRED } from './errorCode';
import { setGlobal } from 'store/global/reducer';

let isFormChanging = false;
let isRefreshingToken = false;

export const waitUntilNoTokenRefresh = async () => {
    for (let i = 0; i < 10000; i++) {
        if (!isRefreshingToken) {
            break;
        }

        await sleep(1000);
    }

    return true;
};

export const when = (condition, render) => (condition ? render() : null);

export const applicantHasReportPermission = (reportData) => {
    const { user } = getStore().getState();
    const ci = reportData?.ciAndCid ?? [];
    const pi = reportData?.piAndPid ?? [];
    const email = user.detail.email.toLowerCase();
    const site = reportData?.siteInstruction;

    let hasPermission = false;

    if (site && site !== 'Study Info') {
        pi.forEach((item) => {
            if (item.toLowerCase() === email) {
                hasPermission = true;
            }
        });

        return hasPermission;
    } else {
        ci.forEach((item) => {
            if (item.toLowerCase() === email) {
                hasPermission = true;
            }
        });

        return hasPermission;
    }
};

// report data is required if application status is in "Draft"
export const postApprovalPermissionChecking = ({ currentTab, status, documentType, reportData }) => {
    const { user } = getStore().getState();

    if (currentTab === TAB_CONSOLIDATION) {
        // Screen of View only when check consolidation APPROVED, ACKNOWLEDGED, DISAPPROVED
        return ![APPROVED, ACKNOWLEDGED, DISAPPROVED].includes(status);
    }

    if (currentTab === PostApprovalFormType.COMMENT) {
        return true;
    }

    // cti only need to check if returned status of report, status === null is not applicable for CTI
    if ([CTC, CTI].includes(documentType)) {
        if (user.role === APPLICANT && status === RETURNED) {
            return applicantHasReportPermission(reportData);
        }
    }

    // the form has not yet created
    if (user.role === APPLICANT && status === null) {
        return true;
    }

    // for other report case
    if (user.role === APPLICANT && [DRAFT, RETURNED].includes(status)) {
        return applicantHasReportPermission(reportData);
    }

    // if not the above case, mainly not applicant case
    return false;
};

export const checkBackendVersionForUpdate = () => {
    const backendVersion = getStore().getState().global.backendVersion;

    // if backend version is updated tell the frontend version to reload if user is logout
    api.version.getBackendVersion().then((res) => {
        if (backendVersion !== res.data) {
            window.location.reload();
        }
    });
};

export const updateIsRefreshToken = (isUpdating) => {
    isRefreshingToken = isUpdating;
};

export const getIsRefreshingToken = () => {
    return isRefreshingToken;
};

export const getNotificationCountOfUnread = () => {
    const { dispatch } = getStore();

    dispatch({
        type: FETCH_NOTIFICATION_COUNT_OF_UNREAD,
        payload: {},
    });
};

export const compareEmail = (email1, email2) => {
    return email1?.toLowerCase() === email2?.toLowerCase();
};

export const updateFormChanging = (isUpdate) => {
    isFormChanging = isUpdate;
};

export const getFormChanging = () => {
    return isFormChanging;
};

export const hasAmendmentDetails = async (formId) => {
    const res = await api.amendment.getAmendmentForm(formId);
    return res.data.result?.amendmentForm?.id !== 0;
};

export const handleRefreshTokenException = async (result) => {
    const store = getStore();

    if (result.data?.status === DATA_NOT_FOUND) {
        store.dispatch(
            setGlobal({
                isErrorShow: true,
                errorTitle: 'Session Expired',
                errorMessage: result.data?.errorMessage,
            }),
        );

        store.dispatch({
            type: LOGOUT_USER,
        });

        throw new Error(result.data?.errorMessage || 'no message');
    } else if (result.data?.status === EXPIRED) {
        store.dispatch({
            type: LOGOUT_USER,
        });

        window.location.replace('/?post-dialog=session-expired');

        throw new Error('session expired');
    }
};

export const amendmentClearMessage = (
    <div key={'message'}>
        <div>- Reason for change </div>
        <div style={{ marginTop: 5 }}>- Proposed by </div>
        <div style={{ marginTop: 5 }}>- Will change increase risk to participants?</div>
        <div style={{ marginTop: 5 }}>- Actual Study Start Date</div>
    </div>
);

const topics = {};

export const subscribe = (topic, fn) => {
    if (!topics[topic]) topics[topic] = {};
    const id = generateRandomId();
    topics[topic][id] = fn;
    return () => {
        topics[topic][id] = null;
        delete topics[topic][id];
    };
};

export const sleep = async (ms) => {
    await new Promise((r) => setTimeout(r, ms));
};

export const publish = async ({ topic, args, callback }) => {
    if (!topics[topic]) return;

    const listOfCurrentTopic = Object.values(topics[topic]);

    for (let i = 0; i < listOfCurrentTopic.length; i++) {
        const currentTopic = listOfCurrentTopic[i];
        await currentTopic(args, callback);
    }
};

export const downloadLocalFile = (file) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);

    const blob = new Blob([file], { type: 'application/pdf' });

    const objectURL = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectURL;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const produce = (base, processFunction) => {
    if (typeof base === 'function') {
        return (prevState) => {
            return updateState(base)(prevState);
        };
    } else {
        return updateValue(base, processFunction);
    }
};

export const isSiteAddedInAmendment = (applicationDetail, siteInfoId) => {
    const siteInfo = applicationDetail.siteInfo.find((item) => item.id === siteInfoId);

    if (!siteInfo) {
        console.error('no site is found by the siteInfo');
        return false;
    }

    const isSiteAddedInAmendment =
        new Date(siteInfo.createDtm).getTime() > new Date(applicationDetail.applicationForm.createDtm).getTime() + 5000;

    return isSiteAddedInAmendment;
};

export const checkIfSiteIdExistInApplication = async () => {};

export const waitUntilFormUpdate = async () => {
    let hasChange = false;

    if (isFormChanging) {
        hasChange = true;
        await sleep(400);
    }

    return hasChange;
};

export const toHalfWidthUnicode = (chars) => {
    try {
        let ascii = '';
        for (let i = 0, l = chars.length; i < l; i++) {
            let c = chars[i].charCodeAt(0);

            // make sure we only convert half-full width char
            if (c >= 0xff00 && c <= 0xffef) {
                c = 0xff & (c + 0x20);
            }

            ascii += String.fromCharCode(c);
        }

        return ascii;
    } catch (error) {
        console.log('%c 120149 general.js error', 'background: green', error);
        return chars;
    }
};

export const isFormChanged = async () => {
    if (isFormChanging) {
        await sleep(300);
    }

    const { applicationDetailPostData } = getStore().getState();
    const { deleteRequest, postData, removeRequest } = applicationDetailPostData;

    let hasChange = false;

    if (!isEmpty(deleteRequest) || !isEmpty(postData) || !isEmpty(removeRequest)) {
        hasChange = true;
    }

    return hasChange;
};

export const handleFileVirus = (error) => {
    if (error?.response?.data?.filename) {
        return { fileName: error?.response?.data?.filename, hasVirus: true };
    }

    return { hasVirus: false };
};

export const permissionChecking = ({
    tabType, // either info or hospital or amendment or comment
    siteInfoId,
    applicationDetail,
    isAmendmentCreate = false,
    isAmendmentEdit = false,
    amendmentType, // either study or site
    amendId,
}) => {
    if ([TAB_AMENDMENT, TAB_COMMENT, TAB_REVIEWER_COMMENT, TAB_MEETINGARRANGE].includes(tabType)) {
        return true;
    }

    const { user, system } = getStore().getState();

    const currentUserEmail = user.detail.email;
    const currentRole = user.role;
    const appStatus = applicationDetail.status?.statusId;

    const allConfig = system?.allConfig;
    const commentConfig = allConfig?.list?.find((item) => item?.code === 'comment');
    const codeValueArr = commentConfig?.codeValue?.split(';'); // multi flow split by ";"
    const commentPermission = codeValueArr?.includes(applicationDetail?.applicationForm?.workflow);

    if (tabType === TAB_CONSOLIDATION && appStatus === COMPLETED) {
        return false;
    }

    if (tabType === TAB_CONSOLIDATION) {
        // Screen of View only when check consolidation APPROVED, ACKNOWLEDGED, DISAPPROVED
        return ![APPROVED, ACKNOWLEDGED, DISAPPROVED].includes(appStatus);
    }

    // enable edit if user role is applicant, cco, cluster admin or secretary
    if (![APPLICANT, CCO, CLUSTER_ADMIN, SECRETARY].includes(currentRole)) {
        return false;
    }

    // check if is amendment create
    if (isAmendmentCreate) {
        // checking permission in amendment form
        if (amendmentType === 'study' && tabType === 'info') {
            return true;
        }

        if (amendmentType === 'site') {
            // eslint-disable-next-line eqeqeq
            if (tabType === 'hospital' && siteInfoId == amendId) {
                return true;
            }
        }

        return false;
    }

    if (!isAmendmentEdit && commentPermission) {
        if ((appStatus === SUBMITTED || appStatus === RE_SUBMITTED) && (currentRole === CCO || currentRole === SECRETARY)) {
            return true;
        }
        if (appStatus === DISTRIBUTED && currentRole === CLUSTER_ADMIN) {
            return true;
        }
    }
        
    // enable edit if status is draft or returned
    if (appStatus === DRAFT || appStatus === RETURNED) {
        if (currentRole === CCO || currentRole === SECRETARY || currentRole === CLUSTER_ADMIN) {
            return false;
        }
        // check if is amendment edit
        if (isAmendmentEdit) {
            if (tabType === 'info') {
                let hasPermission = false;

                // check if email is authorized to the study-info
                if (currentUserEmail?.toLowerCase() === applicationDetail.applicationForm.email?.toLowerCase()) {
                    hasPermission = true;
                }

                if (currentUserEmail?.toLowerCase() === applicationDetail.historyCi?.toLowerCase()) {
                    hasPermission = true;
                }

                applicationDetail.ciDelegation.forEach((item) => {
                    if (item.email?.toLowerCase() === currentUserEmail?.toLowerCase()) {
                        hasPermission = true;
                    }
                });

                // if underAmendment is false, disable permission
                if (applicationDetail.applicationForm.underAmendment === false) {
                    hasPermission = false;
                }

                return hasPermission;
            } else {
                const allowedSite = [...applicationDetail.pi, ...applicationDetail.piDelegation].find(
                    (item) =>
                        currentUserEmail?.toLowerCase() === item.email?.toLowerCase() &&
                        item.siteInfo.id === siteInfoId,
                )?.siteInfo;

                // no permission
                if (!allowedSite) {
                    return false;
                }

                const isNewlySite = isSiteAddedInAmendment(applicationDetail, siteInfoId);

                if (!isNewlySite) {
                    if (allowedSite?.id === siteInfoId && allowedSite?.underAmendment === true) {
                        return true;
                    }
                } else {
                    // if the site is send to ci, disable form editing
                    if (allowedSite.status === 1) {
                        return false;
                    } else {
                        return true;
                    }
                }
                return false;
            }
        }

        // check if the user is authorize to edit study-info with application id: applicationDetail.applicationForm.id
        if (tabType === 'info') {
            let hasPermission = false;

            if (currentUserEmail?.toLowerCase() === applicationDetail.applicationForm.email?.toLowerCase()) {
                hasPermission = true;
            }

            applicationDetail.ciDelegation.forEach((item) => {
                if (item.email?.toLowerCase() === currentUserEmail?.toLowerCase()) {
                    hasPermission = true;
                }
            });

            return hasPermission;
        } else {
            // check if the user is authorize to edit site-info

            // check if the application is in draft && has send to ci, disable edit function of site
            const currentSiteInfo = applicationDetail.siteInfo.find((item) => item.id === siteInfoId);
            // status === 1: the form is submitted to ci
            if (currentSiteInfo?.status === 1) {
                return false;
            }

            // check if the user are one of the site in site-info
            const allowedSiteId = [...applicationDetail.pi, ...applicationDetail.piDelegation].find(
                (item) =>
                    currentUserEmail?.toLowerCase() === item.email?.toLowerCase() && item.siteInfo.id === siteInfoId,
            )?.siteInfo?.id;

            if (allowedSiteId === siteInfoId) {
                return true;
            }

            return false;
        }
    }

    return false;
};

export const getDialogContent = (type) => {
    if (type === 'session-expired') {
        return {
            description: LOGIN_SESSION_EXPIRED,
            title: 'Session Expired',
        };
    } else if (type === 'multiple-login') {
        return {
            description: MULTIPLE_LOGIN_LOGOUT,
            title: 'Session Expired',
        };
    }

    return {
        description: 'null',
        title: 'null',
    };
};

export const getClusterByWorkflow = (workflow, isCI = false) => {
    // remove workflow related filtering (Site PI only) by HA user on 19/1/2024
    if (!isCI) {
        return ['HKEC', 'HKWC', 'KCC', 'KEC', 'KWC', 'NTEC', 'NTWC', 'HAHO', 'OTHERS'];
    }

    if (workflow === 'I') {
        return ['HKEC', 'HKWC', 'KCC', 'KEC', 'KWC', 'NTEC', 'NTWC', 'HAHO'];
    } else if (workflow === 'H') {
        return ['HKWC', 'OTHERS'];
    } else if (workflow === 'N') {
        return ['NTEC', 'OTHERS'];
    } else {
        return [];
    }
};

export const isAuthorizedEmail = (email = '') => {
    const store = getStore();

    const { emailDomain } = store.getState();

    const isExist = emailDomain?.emailDomainList?.some((item) => {
        const inputEmailDomain = email.split('@')?.[1];
        return item.email?.toLowerCase() === inputEmailDomain?.toLocaleLowerCase();
    });

    return isExist;
};

export const getReplacedValue = (value, replacedValue) => {
    let result = value;

    if (replacedValue !== null && replacedValue !== undefined) {
        result = replacedValue;
    }

    return result;
};

export const initializeTableData = ({ tableKey, currentTableData, siteInfoId }) => {
    const { applicationDetailPostData } = getStore().getState();
    const tempTableData = cloneDeep(currentTableData);

    let tablePostData;

    // table belongs to site
    if (siteInfoId) {
        tablePostData =
            applicationDetailPostData.postData?.[tableKey]?.filter((item) => item?.siteInfo?.id === siteInfoId) ?? [];
    } else {
        // table belongs to study-info
        tablePostData =
            applicationDetailPostData.postData?.[tableKey]?.filter((item) => item.siteInfo === undefined) ?? [];
    }

    tablePostData.forEach((dataItem) => {
        const index = tempTableData.findIndex((rowItem) => {
            return rowItem.id === dataItem.id;
        });

        if (index !== -1) {
            tempTableData[index] = dataItem;
        } else {
            tempTableData.push(dataItem);
        }
    });

    // handle delete case of table
    applicationDetailPostData?.deleteRequest?.[tableKey]?.forEach((deleteRequestItem) => {
        const index = tempTableData.findIndex((item) => item.id === deleteRequestItem.id);
        if (index !== -1) {
            tempTableData.splice(index, 1);
        }
    });

    return tempTableData;
};

export const getTimeWithTimeZone = (dateString) => {
    // adding 28800 second to the utc timestamp, and force frontend to display in Hong kong timezone (+08:00).
    const currentTimestamp = new Date(dateString).getTime() + 28800 * 1000;
    return moment(currentTimestamp).utc();
};

export const addRemoveRequest = ({ applicationFormId, name, siteInfoId = -1 }) => {
    const { getState, dispatch } = getStore();
    const { applicationDetailPostData } = getState();
    applicationDetailPostData.applicationFormId = applicationFormId;

    // case of adding remove request to the site
    if (siteInfoId !== -1) {
        if (!applicationDetailPostData.removeRequest?.siteInfo) {
            applicationDetailPostData.removeRequest.siteInfo = {};
        }

        if (!applicationDetailPostData.removeRequest.siteInfo?.[siteInfoId]) {
            applicationDetailPostData.removeRequest.siteInfo[siteInfoId] = [];
        }

        if (!applicationDetailPostData.removeRequest.siteInfo[siteInfoId].includes(name)) {
            if (typeof name === 'string') {
                applicationDetailPostData.removeRequest.siteInfo[siteInfoId].push(name);
            } else {
                applicationDetailPostData.removeRequest.siteInfo[siteInfoId].push(...name);
            }
        }
    } else {
        if (!applicationDetailPostData.removeRequest?.applicationForm) {
            applicationDetailPostData.removeRequest.applicationForm = {};
        }

        if (!applicationDetailPostData.removeRequest.applicationForm[applicationFormId]) {
            applicationDetailPostData.removeRequest.applicationForm[applicationFormId] = [];
        }

        if (!applicationDetailPostData.removeRequest.applicationForm[applicationFormId].includes(name)) {
            if (typeof name === 'string') {
                applicationDetailPostData.removeRequest.applicationForm[applicationFormId].push(name);
            } else {
                applicationDetailPostData.removeRequest.applicationForm[applicationFormId].push(...name);
            }
        }
    }

    dispatch({
        type: SET_APPLICATION_DETAIL_POST_DATA,
        payload: applicationDetailPostData,
    });
};

export const deleteRemoveRequest = ({ siteInfoId = -1, keys = [], applicationFormId = -1 }) => {
    const { dispatch } = getStore();

    dispatch({
        type: DELETE_REMOVE_REQUEST,
        payload: {
            applicationFormId,
            siteInfoId,
            keys,
        },
    });
};

export const validateEmail = (email) => {
    const re = new RegExp(CommonRegex.REGEX_EMAIL);
    return re.test(String(email).toLowerCase());
};

export const updateValue = (base, processFunction) => {
    let newBase = _.cloneDeep(base);
    processFunction(newBase);
    return newBase;
};

export const updateState = (processFunction) => (prevState) => {
    let newBase = _.cloneDeep(prevState);
    processFunction(newBase);
    return newBase;
};

export const getClusterDisplayFromHospital = (hospitalCode) => {
    const store = getStore();
    const hospitals = store.getState().system?.hospitals ?? [];

    const clusterCode = hospitals?.find((item) => {
        const isExist = item.hospital.some((item) => {
            return item.hospitalCode === hospitalCode;
        });

        return isExist;
    })?.clusterCode;

    return clusterCode;
};

export const getClusterDisplayFromId = (clusterId) => {
    const store = getStore();

    const hospitals = store.getState().system?.hospitals ?? [];
    return hospitals.find((item) => item.clusterId === clusterId)?.clusterCode ?? 'Not Found';
};

const hasNumber = (string) => {
    return new RegExp(CommonRegex.REGEX_NUMBER).test(string);
};

const hasLowerCase = (string) => {
    return new RegExp(CommonRegex.REGEX_LOWER_CASE).test(string);
};

const hasUpperCase = (string) => {
    return new RegExp(CommonRegex.REGEX_UPPER_CASE).test(string);
};

export const checkPasswordValidity = (passCode = '') => {
    if (passCode.length < 8) {
        return false;
    }

    if (!hasNumber(passCode)) {
        return false;
    }

    if (!hasLowerCase(passCode)) {
        return false;
    }

    if (!hasUpperCase(passCode)) {
        return false;
    }

    return true;
};

export const generateRandomId = (length = 20) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

export const isPendingCheck = (application, user) => {
    const { statusId, siteResponseList, pendingReview } = application;

    if (user.role !== APPLICANT && user.role !== REVIEWER) {
        return false;
    }

    if (user.role === REVIEWER && pendingReview === true) {
        return true;
    }

    // only draft and returned status with applicant role will display the pending indicator
    if (statusId === DRAFT || statusId === RETURNED) {
        const userDetail = user.detail;

        // user is not loaded
        if (!userDetail) {
            return false;
        }

        const isCI = application?.ci?.some((item) => item.toLowerCase() === userDetail.email.toLowerCase());

        if (isCI) {
            // in draft or return status, ci need to submit or wait site to finish their task
            if (statusId === DRAFT || statusId === RETURNED) {
                return true;
            }
        }

        let isSitePI = false;
        let institution = '';

        Object.entries(application.pi).forEach(([institutionKey, listOfPI]) => {
            listOfPI.forEach((item) => {
                if (item.toLowerCase() === userDetail.email.toLowerCase()) {
                    isSitePI = true;
                    institution = institutionKey;
                }
            });
        });

        if (isSitePI) {
            const currentSiteStatus = siteResponseList.find((item) => item.code === institution);
            return !currentSiteStatus?.completed;
        }
    }

    return false;
};

export const getUserPermissions = (role) => {
    const currentUser = userRoles.find((item) => item.key === role);

    return currentUser.folder
        .map((item) => {
            return item.display !== '-' ? item.key : null;
        })
        .filter((item) => {
            return item !== null;
        });
};

export const getStatusFromFolder = (folder, userRole) => {
    const currentRoleFolder = userRoles.find((item) => item.key === userRole)?.folder;

    if (!currentRoleFolder) {
        return [];
    }

    return currentRoleFolder.filter((item) => {
        return item.display === folder;
    });
};

export const getFolderDisplayPath = (role, statusKey) => {
    const menu = getStore().getState().menu.data;
    const routes = menu?.find((item) => item.url === '/applications')?.subRoute || [];

    const currentUser = userRoles.find((item) => item.key === role);
    const display = currentUser.folder.find((item) => item.key === statusKey)?.display;

    const url = routes?.find((item) => item.desc === display)?.url || '/all';

    return url;
};

export const generateRandomNumberString = () => {
    return Math.floor(Math.random() * 10000).toString();
};

export const getHospitalItem = (hospitalCode) => {
    const { getState } = getStore();
    const hospitalList = getState().system.formattedHospitalData ?? [];
    const index = hospitalList.findIndex((i) => i.hospitalCode === hospitalCode);
    if (index === -1) {
        return {};
    }
    return hospitalList[index];
};

export const checkCIAmendment = (applicationDetail, userDetail) => {
    const isCIHistory = compareEmail(applicationDetail.historyCi, userDetail?.email);
    // const isCI
    const isCIDelegation = applicationDetail.ciDelegation.some((item) => compareEmail(item.email, userDetail?.email));

    return isCIHistory || isCIDelegation;
};

export const isHighlight = (applicationDetail, siteInfoId, formId, tabType, keys, istable = false) => {
    let ishl = false;
    const changedItem = applicationDetail?.changedItem?.changedItem;
    const siteInfo = changedItem.siteInfo;
    if (tabType === 'hospital') {
        keys.forEach((key) => {
            if (!istable) {
                if (siteInfo && siteInfo[`${siteInfoId}`] && siteInfo[`${siteInfoId}`].indexOf(key) > -1) {
                    ishl = true;
                    return;
                }
            } else {
                if (
                    // eslint-disable-next-line eqeqeq
                    changedItem[key] != undefined &&
                    changedItem[key][siteInfoId] !== undefined &&
                    changedItem[key][siteInfoId].length
                ) {
                    ishl = true;
                    return;
                }
            }
        });
    } else {
        keys.forEach((key) => {
            if (!istable) {
                if (
                    changedItem &&
                    changedItem.applicationForm &&
                    changedItem.applicationForm[`${formId}`] &&
                    changedItem.applicationForm[`${formId}`].indexOf(key) > -1
                ) {
                    ishl = true;
                    return;
                }
            } else {
                if (
                    // eslint-disable-next-line eqeqeq
                    changedItem[key] != undefined &&
                    changedItem[key][formId] !== undefined &&
                    changedItem[key][formId].length
                ) {
                    ishl = true;
                    return;
                }
            }
        });
    }
    return ishl;
};

export const emptyShow = (text) => {
    if (!text) return '-';
    return text;
};

export const piDelegationColumns = [
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'title', headerName: 'Title', width: 130, tooltip: 'e.g. Dr/Mr/Mrs/Ms/Prof.' },
    {
        field: 'surname',
        headerName: 'Surname',
        width: 120,
        valueGetter: (params) => params.getValue('surname'),
    },
    {
        field: 'firstName',
        headerName: 'First name',
        width: 120,
        valueGetter: (params) => params.getValue('firstName'),
    },
    { field: 'cluster', headerName: 'Cluster', width: 100 },
    {
        field: 'institution',
        headerName: 'Hospital / Institution',
        // tooltip: (
        //     <div>
        //         <div>Central IRB: Site PI shall be an employee/appointee of HA</div>
        //         <div style={{ marginTop: 15 }}>
        //             HKU/HA HKW IRB: Site PI shall be an employee/appointee/student of the HKU or HA HKWC
        //         </div>
        //         <div style={{ marginTop: 15 }}>
        //             Joint CUHK-NTEC CREC: Site PI shall be an employee/appointee/student of the CUHK or HA NTEC
        //         </div>
        //     </div>
        // ),
        width: 120,
    },
    {
        field: 'department',
        headerName: 'Department',
        width: 160,
    },
];

export const ciDelegationColumns = [
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'title', headerName: 'Title', width: 130, tooltip: 'e.g. Dr/Mr/Mrs/Ms/Prof.' },
    {
        field: 'surname',
        headerName: 'Surname',
        width: 120,
        valueGetter: (params) => params.getValue('surname'),
    },
    {
        field: 'firstName',
        headerName: 'First name',
        width: 120,
        valueGetter: (params) => params.getValue('firstName'),
    },
    { field: 'position', headerName: 'Position', width: 100 },
    { field: 'cluster', headerName: 'Cluster', width: 100 },
    {
        field: 'institution',
        headerName: 'Hospital / Institution',
        // tooltip: (
        //     <div>
        //         <div>Central IRB: Site PI shall be an employee/appointee of HA</div>
        //         <div style={{ marginTop: 15 }}>
        //             HKU/HA HKW IRB: Site PI shall be an employee/appointee/student of the HKU or HA HKWC
        //         </div>
        //         <div style={{ marginTop: 15 }}>
        //             Joint CUHK-NTEC CREC: Site PI shall be an employee/appointee/student of the CUHK or HA NTEC
        //         </div>
        //     </div>
        // ),
        width: 120,
    },
    {
        field: 'department',
        headerName: 'Department',
        width: 160,
    },
];
